import React, { useState } from "react";
import { AlertCircle, LockKeyhole, X } from "lucide-react";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import type {
  AutoConfirmOverview,
  AutoConfirmSubscription,
  UpdateAutoConfirmAccount,
} from "../../../../domain/types/bank-transfer-auto-confirm";
import {
  BANKS,
  BankMark,
  Field,
  ModalShell,
  inputClass,
  requiresIdentity,
  requiresWeb,
  requiresWebPassword,
} from "./shared";

export const AccountEditModal = ({
  companyId,
  item,
  onClose,
  onDone,
}: {
  companyId: number;
  item: AutoConfirmOverview["subscriptions"][number];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) => {
  const [form, setForm] = useState<UpdateAutoConfirmAccount>({
    bankAccountPassword: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const bankName = BANKS.find((bank) => bank.value === item.bank)?.label ?? item.bank;
  const needsWebId = requiresWeb.has(item.bank);
  const needsWebPassword = requiresWebPassword.has(item.bank);
  const needsIdentity = requiresIdentity.has(item.bank);

  const filled =
    form.bankAccountPassword.trim().length > 0 &&
    (!needsWebId || !!form.webId?.trim()) &&
    (!needsWebPassword || !!form.webPassword?.trim()) &&
    (!needsIdentity || !!form.identityNumber?.trim());

  const submit = async () => {
    if (!filled || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await bankTransferAutoConfirmApiService.updateAccount(
        companyId,
        item.bankTransferId,
        form,
      );
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "계좌 정보 변경에 실패했습니다.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[92vh] w-full max-w-[560px] overflow-auto rounded-[12px] bg-white shadow-[0_20px_60px_rgba(6,18,44,0.20)]">
        <div className="flex items-center gap-3 border-b border-[#e8eaf0] px-6 py-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EBF3FF] text-[#4285F4]">
            <LockKeyhole size={19} />
          </span>
          <div>
            <h2 className="text-base font-bold text-[#232936]">계좌 정보 수정</h2>
            <p className="mt-0.5 text-xs text-[#8991a1]">
              은행 비밀번호나 빠른조회 계정이 바뀌었을 때 갱신합니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg bg-[#f3f4f6] p-2 text-[#7c8492]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="mb-4 flex items-center gap-3 rounded-[10px] bg-[#f7f8fb] px-4 py-3">
            <BankMark bank={item.bank} small />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-[#2d3340]">{bankName}</div>
              <div className="mt-0.5 truncate text-[11px] text-[#8a92a2]">
                {item.accountNumber} · {item.accountHolder}
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-xl border border-[#dce6f8] bg-[#f3f7ff] px-4 py-3 text-[11px] leading-5 text-[#4a5f8a]">
            은행 · 계좌번호 · 계좌 종류는 바꿀 수 없습니다. 다른 계좌를 쓰시려면{" "}
            <b>새 계좌로 등록</b>해 주세요.
          </div>

          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              <AlertCircle size={15} /> {error}
            </div>
          )}

          <div className="space-y-3">
          <Field label="계좌 비밀번호">
            <input
              type="password"
              value={form.bankAccountPassword}
              onChange={(e) =>
                setForm({ ...form, bankAccountPassword: e.target.value })
              }
              maxLength={4}
              placeholder="••••"
              className="w-full rounded-lg border border-[#dfe2e8] px-3 py-2.5 text-sm outline-none focus:border-[#4285F4]"
            />
          </Field>

          {needsWebId && (
            <Field label="빠른조회 아이디">
              <input
                value={form.webId ?? ""}
                onChange={(e) => setForm({ ...form, webId: e.target.value })}
                placeholder="은행 인터넷뱅킹 아이디"
                className="w-full rounded-lg border border-[#dfe2e8] px-3 py-2.5 text-sm outline-none focus:border-[#4285F4]"
              />
            </Field>
          )}

          {needsWebPassword && (
            <Field label="빠른조회 비밀번호">
              <input
                type="password"
                value={form.webPassword ?? ""}
                onChange={(e) => setForm({ ...form, webPassword: e.target.value })}
                className="w-full rounded-lg border border-[#dfe2e8] px-3 py-2.5 text-sm outline-none focus:border-[#4285F4]"
              />
            </Field>
          )}

          {needsIdentity && (
            <Field label="사업자번호">
              <input
                value={form.identityNumber ?? ""}
                onChange={(e) =>
                  setForm({ ...form, identityNumber: e.target.value.replace(/\D/g, "") })
                }
                maxLength={10}
                placeholder="숫자 10자리"
                className="w-full rounded-lg border border-[#dfe2e8] px-3 py-2.5 text-sm outline-none focus:border-[#4285F4]"
              />
            </Field>
          )}
          </div>

          <div className="mt-5 rounded-[10px] bg-[#f7f8fb] px-4 py-4 text-[11px] leading-6 text-[#77808f]">
            <div className="mb-1.5 text-xs font-bold text-[#3b4251]">
              수정하기 전에 확인해 주세요
            </div>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                저장된 값은 보안상 다시 표시하지 않습니다.{" "}
                <b>바꾸지 않는 항목도 다시 입력</b>해 주세요.
              </li>
              <li>
                은행 확인이 실패하면 <b>기존 정보가 그대로</b> 유지됩니다.
              </li>
              <li>
                수정에는 <b>요금이 청구되지 않습니다.</b> 이용기간과 다음 결제일도
                바뀌지 않습니다.
              </li>
              <li>
                계좌 정보 수정은 <b>회사 대표 계정</b>만 할 수 있습니다.
              </li>
            </ul>
          </div>
        </div>

        <div className="flex gap-2 border-t border-[#e8eaf0] px-6 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#dfe2e8] py-3 text-sm font-bold text-[#596172] hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={() => void submit()}
            disabled={!filled || submitting}
            className="flex-1 rounded-lg bg-[#4285F4] py-3 text-sm font-bold text-white hover:bg-[#3367D6] disabled:bg-[#aab3cc]"
          >
            {submitting ? "은행에 확인 중" : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
};


const CardMeta = ({ label, value }: { label: string; value: string }) => (
  <span className="min-w-0">
    <small className="block text-[9px] font-semibold text-[#9aa1af]">{label}</small>
    <b className="mt-1 block truncate text-[11px] font-bold text-[#3b4251]">{value}</b>
  </span>
);

